import React, { FC, useCallback, useContext, useMemo } from 'react';
import { GuardedRouteLink, useIsGuardedRouteAllowed } from '../resources/GuardedRouteLink';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { apiUser } from '../resources/api/apiUser';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { GridColumns, GridRowIdGetter } from '@mui/x-data-grid';
import { UrlQueryDataGrid } from '../components/UrlQueryDataGrid/UrlQueryDataGrid';
import { getDateValueFormatter } from '../valueFormatters/DateValueFormatter';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../filterOperators/dateFilterOperators';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import Button from '@mui/material/Button';
import { IconButton } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useDialog } from '../hooks/useDialog';
import { ConfirmationDialog } from '../components/ConfirmationDialog/ConfirmationDialog';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { IRole } from '../resources/models/IRole';
import { IBasicUserInfo } from '../resources/models/IUserInfo';
import { AssignRoleModal } from '../components/AssignRoleModal/AssignRoleModal';
import { RolesFetcher } from '../fetchers/RolesFetcher';
import { UserFullInfoFetcher } from '../fetchers/UserFullInfoFetcher';

const { useRevokeUserRole, useAssignRole } = apiUser;

const getRowId: GridRowIdGetter = (row) => {
  return `${row.id} ${row.organization_name}`;
};

interface IRevokeRoleProps {
  user: IBasicUserInfo;
  role: IRole;
}

const RevokeUserRoleButton: FC<IRevokeRoleProps> = ({ user, role }) => {
  const { execute, Guard } = useRevokeUserRole(user.id, role.id);
  const {
    isOpen: isDeleting,
    openUrl: deleteUrl,
    close: abortDelete,
  } = useDialog(`${role.id}_${role.organization_name}`, 'delete');
  const handleError = useApiErrorHandler();
  const { onRefresh } = useContext(UserFullInfoFetcher.Context);
  const { t } = useTranslation();

  const onDelete = useCallback(async () => {
    try {
      await execute(role.organization_name);
      await onRefresh();
      abortDelete();
    } catch (e) {
      handleError(e);
    }
  }, [execute, role.organization_name]);

  return (
    <div>
      <Guard>
        <IconButton component={RouterLink} to={deleteUrl} replace={true}>
          <Delete />
        </IconButton>
        <ConfirmationDialog
          open={isDeleting}
          title={t('userRoles.revoke.title')}
          message={
            <div>
              {t(role.organization_name ? 'userRoles.revoke.descriptionOrganization' : 'userRoles.revoke.description', {
                role: role.name,
                userName: `${user.first_name} ${user.last_name}`,
                organization: role.organization_name,
              })}
            </div>
          }
          confirm={onDelete}
          abort={abortDelete}
        />
      </Guard>
    </div>
  );
};

const noReplaceOpts = { replace: false };

const UserRoles: FC = () => {
  const { t } = useTranslation();
  const { data } = useContext(UserFullInfoFetcher.Context);

  const isAllUsersAllowed = useIsGuardedRouteAllowed('AllUsers', []);
  const isUserDetailsAllowed = useIsGuardedRouteAllowed('User', [data?.id || 'any']);

  const { Guard: GuardAssignRole } = useAssignRole(data?.id);

  const { isOpen: isAssigning, openUrl: assignUrl, close: abortAssigning } = useDialog('assign', void 0, noReplaceOpts);

  const crumbs = useMemo(() => {
    const out = [
      {
        title: t('layout.navigation.users'),
        url: isAllUsersAllowed ? AuthenticatedRoutes.AllUsers() : void 0,
      },
      {
        title: data ? `${data.first_name} ${data.last_name}` : '...',
        url: isUserDetailsAllowed && data ? AuthenticatedRoutes.User(data.id) : void 0,
      },
    ];
    return out;
  }, [data, isAllUsersAllowed, isUserDetailsAllowed, t]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'name',
        headerName: 'Role name',
        flex: 1,
        renderCell: (params) => (
          <Tooltip title={params.row.description}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: 'organization_name',
        headerName: 'Organization Name',
        flex: 1,
        renderCell: (params) =>
          params.value ? (
            <Link component={GuardedRouteLink} route={'OrganizationDetails'} params={[params.value]} showFallback>
              {params.value}
            </Link>
          ) : null,
      },
      {
        field: 'created_at',
        headerName: t('common.tableHeader.created'),
        width: 130,
        valueFormatter: getDateValueFormatter(),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'updated_at',
        headerName: t('common.tableHeader.updated'),
        width: 130,
        valueFormatter: getDateValueFormatter(),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'actions',
        headerName: ' ',
        width: 120,
        renderCell: (params) => (data ? <RevokeUserRoleButton user={data} role={params.row} /> : null),
      },
    ],
    [data]
  );

  if (!data) return null;

  return (
    <div>
      <PageHeader title={'Roles'} crumbs={crumbs}>
        <GuardAssignRole>
          <Button component={RouterLink} to={assignUrl} variant="contained">
            {t('userRoles.assignRole.button')}
          </Button>
          <RolesFetcher.WMF>
            <AssignRoleModal isOpen={isAssigning} closeModal={abortAssigning} />
          </RolesFetcher.WMF>
        </GuardAssignRole>
      </PageHeader>

      <UrlQueryDataGrid
        columns={columns}
        rows={data.roles}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        paginationMode={'client'}
        getRowId={getRowId}
      />
    </div>
  );
};

export const UserRolesPage: FC = () => {
  return (
    <UserFullInfoFetcher.WAF>
      <UserRoles />
    </UserFullInfoFetcher.WAF>
  );
};
